/**
 * JSON-LD Schema helpers for Technical SEO
 */

import { siteConfig } from '../config';

const siteUrl = siteConfig.siteUrl;
const siteName = siteConfig.siteName;

export interface Game {
  slug: string;
  title: string;
  description: string;
  thumbnail: string;
  playUrl: string;
  developer?: string;
  rating?: string;
  votes?: string;
  released?: string;
  publishedDate?: string;
  lastUpdated?: string;
  categories: string[];
}

/**
 * Organization & WebSite Schema for Homepage
 */
export function buildHomePageSchema() {
  const organization = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteName,
    url: siteUrl,
    logo: {
      '@type': 'ImageObject',
      url: `${siteUrl}/assets/img/logo.png`,
    },
    sameAs: [
      // Add social media profiles here if available
    ],
  };

  const website = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: siteName,
    url: siteUrl,
    inLanguage: 'en',
    description: 'Play the best free online games instantly in your browser. No downloads required!',
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${siteUrl}/search?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };

  return {
    '@context': 'https://schema.org',
    '@graph': [organization, website],
  };
}

/**
 * VideoGame Schema for Game Pages
 */
export function buildGameSchema(game: Game) {
  const aggregateRating = game.rating && game.votes ? {
    '@type': 'AggregateRating',
    ratingValue: game.rating,
    ratingCount: game.votes.replace(/,/g, ''),
    bestRating: '5',
    worstRating: '1',
  } : undefined;

  // Use lastUpdated if available, otherwise use current date
  const modifiedDate = game.lastUpdated || new Date().toISOString();
  
  return {
    '@context': 'https://schema.org',
    '@type': 'VideoGame',
    name: game.title,
    description: game.description,
    url: `${siteUrl}/${game.slug}`,
    image: `${siteUrl}${game.thumbnail}`,
    genre: game.categories,
    inLanguage: 'en',
    isAccessibleForFree: true,
    author: game.developer ? {
      '@type': 'Organization',
      name: game.developer,
    } : undefined,
    publisher: {
      '@type': 'Organization',
      name: siteName,
      url: siteUrl,
    },
    datePublished: game.publishedDate || game.released,
    dateModified: modifiedDate,
    applicationCategory: 'Game',
    operatingSystem: 'Web Browser',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
    },
    aggregateRating,
    gamePlatform: ['Web Browser', 'Desktop', 'Mobile'],
    playMode: 'SinglePlayer',
  };
}

/**
 * BreadcrumbList Schema for Game Pages
 */
export function buildGameBreadcrumbSchema(game: Game, categoryName?: string, categorySlug?: string) {
  const items = [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: siteUrl,
    },
  ];

  if (categoryName && categorySlug) {
    items.push({
      '@type': 'ListItem',
      position: 2,
      name: categoryName,
      item: `${siteUrl}/category/${categorySlug}`,
    });
    items.push({
      '@type': 'ListItem',
      position: 3,
      name: game.title,
      item: `${siteUrl}/${game.slug}`,
    });
  } else {
    items.push({
      '@type': 'ListItem',
      position: 2,
      name: game.title,
      item: `${siteUrl}/${game.slug}`,
    });
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items,
  };
}

/**
 * CollectionPage Schema for Category Pages
 */
export function buildCategorySchema(categoryName: string, categorySlug: string, description: string, gamesCount: number) {
  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryName} Games`,
    description,
    url: `${siteUrl}/category/${categorySlug}`,
    mainEntity: {
      '@type': 'ItemList',
      name: `${categoryName} Games`,
      description,
      numberOfItems: gamesCount,
    },
  };
}

/**
 * BreadcrumbList Schema for Category Pages
 */
export function buildCategoryBreadcrumbSchema(categoryName: string, categorySlug: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: siteUrl,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: categoryName,
        item: `${siteUrl}/category/${categorySlug}`,
      },
    ],
  };
}

/**
 * FAQPage Schema for Games with FAQs
 */
export interface FAQItem {
  question: string;
  answer: string;
}

export function buildFAQSchema(faqs: FAQItem[]) {
  if (!faqs || faqs.length === 0) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

/**
 * Combine multiple schemas into a @graph
 */
export function combineSchemas(...schemas: any[]) {
  return {
    '@context': 'https://schema.org',
    '@graph': schemas,
  };
}

/**
 * Combined JSON-LD schema for single-game homepage.
 *
 * Fixes two issues that arise when a game is used as the homepage:
 * 1. VideoGame `url` must be the site root (siteUrl), not `siteUrl/{slug}`,
 *    because the game is served at `/`, not `/{slug}`.
 * 2. Organization + WebSite (with SearchAction) should be present on every
 *    homepage, not only in category-mode.
 *
 * Optional FAQs are merged into the same @graph to avoid multiple ld+json
 * script tags and duplicate @context declarations.
 */
export function buildSingleGameHomeSchema(game: Game, faqs?: FAQItem[]) {
  const aggregateRating = game.rating && game.votes ? {
    '@type': 'AggregateRating',
    ratingValue: game.rating,
    ratingCount: game.votes.replace(/,/g, ''),
    bestRating: '5',
    worstRating: '1',
  } : undefined;

  const thumbnailUrl = game.thumbnail.startsWith('http')
    ? game.thumbnail
    : `${siteUrl}${game.thumbnail}`;

  const videoGame: Record<string, unknown> = {
    '@type': 'VideoGame',
    '@id': `${siteUrl}/#videogame`,
    name: game.title,
    description: game.description,
    // url = homepage root, not /{slug}, because the game IS the homepage
    url: siteUrl,
    image: thumbnailUrl,
    genre: game.categories,
    inLanguage: 'en',
    isAccessibleForFree: true,
    applicationCategory: 'Game',
    operatingSystem: 'Web Browser',
    gamePlatform: ['Web Browser', 'Desktop', 'Mobile'],
    playMode: 'SinglePlayer',
    datePublished: game.publishedDate || game.released,
    dateModified: game.lastUpdated || new Date().toISOString(),
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
    },
    publisher: { '@id': `${siteUrl}/#organization` },
  };

  if (game.developer) {
    videoGame['author'] = { '@type': 'Organization', name: game.developer };
  }
  if (aggregateRating) {
    videoGame['aggregateRating'] = aggregateRating;
  }

  const organization = {
    '@type': 'Organization',
    '@id': `${siteUrl}/#organization`,
    name: siteName,
    url: siteUrl,
    logo: {
      '@type': 'ImageObject',
      url: `${siteUrl}/assets/img/logo-pokerogue.png`,
    },
  };

  const website = {
    '@type': 'WebSite',
    '@id': `${siteUrl}/#website`,
    name: siteName,
    url: siteUrl,
    inLanguage: 'en',
    publisher: { '@id': `${siteUrl}/#organization` },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${siteUrl}/search?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };

  const nodes: unknown[] = [videoGame, organization, website];

  if (faqs && faqs.length > 0) {
    nodes.push({
      '@type': 'FAQPage',
      mainEntity: faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: { '@type': 'Answer', text: faq.answer },
      })),
    });
  }

  return {
    '@context': 'https://schema.org',
    '@graph': nodes,
  };
}
